import { useState } from 'react';
import { useIntl } from 'react-intl';

import {
  AgosFinanceCalculator,
  AgosFinanceCalculatorText,
} from '@/components/FinanceCalculator/AgosFinanceCalculator';
import { CembraFinanceCalculator } from '@/components/FinanceCalculator/CembraFinanceCalculator';
import { CreditPlusFinanceCalculator } from '@/components/FinanceCalculator/CreditPlusFinanceCalculator';
import * as Fc from '@/components/FinanceCalculator/FinanceCalculator.styles';
import { Modal } from '@/components/Modal';
import { ProductDetail } from '@/components/ProductDetail';
import { Typography } from '@/components/Typography';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import type { Money } from '@/graphql/saleor/api';
import type { Maybe } from '@/lib/types';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import * as S from './FinanceCalculatorModal.styles';
import { CembraBenefit4, CembraBenefit5, messages } from './messages';
export interface FinanceCalculatorModalProps {
  handleAddToCart: () => void;
  isOpen: boolean;
  isProcessing: boolean;
  loading: boolean;
  onClose: () => void;
  paymentType: 'cembra' | 'agos' | 'creditPlus';
  price: Maybe<Money>;
  variantCTA?: OrderAppCtaType;
}
export const FinanceCalculatorModal = ({
  handleAddToCart,
  isOpen,
  isProcessing,
  loading,
  onClose,
  paymentType,
  price,
  variantCTA,
}: FinanceCalculatorModalProps) => {
  const { formatMessage } = useIntl();
  const { cembraOpenInvoicePaymentMax } = useConfigProvider();
  const [montlyLoanTerm, setMontlyLoanTerm] = useState(6);
  const { formatPriceString } = useRegionProvider();

  const addToCart = () => {
    handleAddToCart();
    onClose();
  };

  return (
    <Modal
      data-test-id='FinanceCalculatorModal-Modal'
      isOpen={isOpen}
      maxWidth='sm'
      onClose={onClose}
    >
      <Modal.Header
        data-test-id='FinanceCalculatorModal-ModalHeader'
        divider
        onClose={onClose}
      >
        <h2>{formatMessage(messages.title)}</h2>
      </Modal.Header>
      <Modal.Body data-test-id='FinanceCalculatorModal-ModalBody'>
        <S.Body data-test-id='FinanceCalculatorModal-Body'>
          <S.BodyContainer>
            {paymentType === 'cembra' && (
              <>
                <Typography>
                  {formatMessage(messages.cembraBenefits)}
                </Typography>
                <S.CembraBenefitList>
                  <li>
                    <Typography>
                      {formatMessage(messages.cembraBenefit1)}
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      {formatMessage(messages.cembraBenefit2)}
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      {formatMessage(messages.cembraBenefit3)}
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      {CembraBenefit4({
                        maxAmount: formatPriceString(
                          {
                            amount: cembraOpenInvoicePaymentMax ?? 10000,
                            currency: price?.currency ?? 'CHF',
                          },
                          {
                            maximumFractionDigits: 0,
                            currencyDisplay: 'symbol',
                          },
                        ),
                      })}
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      {CembraBenefit5({
                        maxAmount: formatPriceString(
                          {
                            amount: cembraOpenInvoicePaymentMax ?? 10000,
                            currency: price?.currency ?? 'CHF',
                          },
                          {
                            maximumFractionDigits: 0,
                            currencyDisplay: 'symbol',
                          },
                        ),
                      })}
                    </Typography>
                  </li>
                </S.CembraBenefitList>
                <Typography
                  fontWeight='bold'
                  marginBottom='s100'
                  textAlign='center'
                  textTransform='uppercase'
                >
                  {formatMessage(messages.selectTermDuration)}
                </Typography>
              </>
            )}

            {!!price &&
              {
                cembra: <CembraFinanceCalculator price={price} />,
                agos: (
                  <AgosFinanceCalculator
                    monthlyLoanTerm={montlyLoanTerm}
                    price={price}
                    selectMonthlyLoanTerm={(value) => setMontlyLoanTerm(value)}
                  />
                ),
                creditPlus: <CreditPlusFinanceCalculator price={price} />,
              }[paymentType]}

            <ProductDetail.CTA
              cta={variantCTA}
              data-test-id='ProductDetailCTA'
              isProcessing={isProcessing}
              loading={loading}
              onClick={addToCart}
            />

            {paymentType === 'agos' && price && (
              <AgosFinanceCalculatorText
                monthlyLoanTerm={montlyLoanTerm}
                price={price}
              />
            )}

            {paymentType === 'creditPlus' && (
              <>
                <Typography marginTop='s150' scale='p3'>
                  {formatMessage(messages.creditPlusMinimumCriteria)}
                </Typography>
                <Fc.BenefitList>
                  <li>
                    <Typography scale='p3'>
                      {formatMessage(messages.creditPlusAge)}
                    </Typography>
                  </li>
                  <li>
                    <Typography scale='p3'>
                      {formatMessage(messages.creditPlusIdentity)}
                    </Typography>
                  </li>
                  <li>
                    <Typography scale='p3'>
                      {formatMessage(messages.creditPlusResidency)}
                    </Typography>
                  </li>
                  <li>
                    <Typography scale='p3'>
                      {formatMessage(messages.creditPlusBankAccount)}
                    </Typography>
                  </li>
                </Fc.BenefitList>

                <Typography marginTop='s150' scale='p3'>
                  {formatMessage(messages.creditPlusAdditionalConditions)}
                </Typography>
                <Fc.BenefitList>
                  <li>
                    <Typography scale='p3'>
                      {formatMessage(messages.creditPlusNonEuCitizens)}
                    </Typography>
                  </li>
                  <li>
                    <Typography scale='p3'>
                      {formatMessage(messages.creditPlusSelfEmployed)}
                    </Typography>
                  </li>
                  <li>
                    <Typography scale='p3'>
                      {formatMessage(messages.creditPlusTraineesStudents)}
                    </Typography>
                  </li>
                </Fc.BenefitList>

                <Typography marginTop='s150' scale='p3'>
                  {formatMessage(messages.creditPlusDisclaimer)}
                </Typography>
              </>
            )}
          </S.BodyContainer>
        </S.Body>
      </Modal.Body>
    </Modal>
  );
};
